// src/components/ImageCarousel.jsx
import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, m } from "framer-motion";
import { createPortal } from "react-dom";


function wrapIndex(min, max, v) {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
}

const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 320 : -320, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction < 0 ? 320 : -320, opacity: 0 }),
};

export default function ImageCarousel({
  images = [],                 // array of image src strings
  altPrefix = "Slide",         // alt text prefix
  className = "",              // extra classes for outer wrapper
  rounded = "rounded-xl",      // rounding class (e.g., rounded-2xl)
  aspect = "aspect-[16/9]",
  fit = "cover",
  enableLightbox = true,       // click to open fullscreen

  // Lightbox controls
  lbAspect = null,
  lbFit = "contain",
}) {
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const count = images.length;
  const fitClass = fit === "contain" ? "object-contain" : "object-cover";

  const paginate = useCallback(
    (dir) => {
      if (!count) return;
      setDirection(dir);
      setPage((p) => wrapIndex(0, count, p + dir));
    },
    [count]
  );

  // Arrow key support (Left/Right) and Enter to open lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") paginate(1);
      if (e.key === "ArrowLeft") paginate(-1);
      if (e.key === "Enter" && enableLightbox) setLightboxOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paginate, enableLightbox]);

  if (count === 0) return null;

  const swipeConfidenceThreshold = 6000;
  const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

  return (
    <div className={`relative ${rounded} overflow-hidden ${aspect} bg-slate-50 ${className}`}>
      <AnimatePresence custom={direction} initial={false}>
        <m.img
          key={page}
          src={images[page]}
          alt={`${altPrefix}${count > 1 ? ` ${page + 1} of ${count}` : ""}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${fitClass} ${fit === 'contain' ? 'bg-white p-2' : ''} ${enableLightbox ? 'cursor-zoom-in' : ''}`}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeConfidenceThreshold) paginate(1);
            else if (swipe > swipeConfidenceThreshold) paginate(-1);
          }}
          onClick={() => enableLightbox && setLightboxOpen(true)}
        />
      </AnimatePresence>

      {/* Prev / Next (only when multiple images) */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => paginate(-1)}
            aria-label="Previous image"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10
                       rounded-full bg-white/90 backdrop-blur p-2 shadow
                       ring-1 ring-black/10 hover:bg-white"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => paginate(1)}
            aria-label="Next image"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10
                       rounded-full bg-white/90 backdrop-blur p-2 shadow
                       ring-1 ring-black/10 hover:bg-white"
          >
            ›
          </button>

          {/* Dots */}
          <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1.5 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDirection(i > page ? 1 : -1);
                  setPage(i);
                }}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2 w-2 rounded-full transition
                  ${i === page ? "bg-blue-600" : "bg-white/70 ring-1 ring-black/10 hover:bg-white"}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Lightbox */}
      {enableLightbox && lightboxOpen &&
        createPortal(
            <AnimatePresence>
            <m.div
                className="fixed inset-0 z-[1200] flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {/* Backdrop */}
                <div
                className="absolute inset-0 bg-black/80"
                onClick={() => setLightboxOpen(false)}
                />

                {/* Wrapper: either fixed aspect + cover, or free + contain */}
                {lbAspect ? (
                // Forced frame (great for uniform look) — use cover/contain inside
                <div className={`relative z-[1201] w-[min(92vw,1100px)] ${lbAspect} rounded-xl overflow-hidden bg-white`}>
                    <m.img
                    key={`lb-${page}`}
                    src={images[page]}
                    alt={`${altPrefix}${count > 1 ? ` ${page + 1} of ${count}` : ""}`}
                    className={`absolute inset-0 w-full h-full ${lbFit === "contain" ? "object-contain" : "object-cover"}`}
                    initial={{ scale: 0.98, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.98, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    />
                </div>
                ) : (
                // Free frame (no forced aspect): contain by default
                <m.img
                    key={`lb-${page}`}
                    src={images[page]}
                    alt={`${altPrefix}${count > 1 ? ` ${page + 1} of ${count}` : ""}`}
                    className="relative z-[1201] max-h-[90vh] max-w-[90vw] object-contain rounded-xl shadow-2xl bg-white"
                    initial={{ scale: 0.98, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.98, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                />
                )}

                {/* Close */}
                <button
                onClick={() => setLightboxOpen(false)}
                aria-label="Close"
                className="absolute top-4 right-4 z-[1202] rounded-md bg-white/90 backdrop-blur px-3 py-1.5
                            text-slate-800 shadow ring-1 ring-black/10 hover:bg-white"
                >
                ✕
                </button>

                {/* Prev/Next */}
                {count > 1 && (
                <>
                    <button
                    onClick={(e) => { e.stopPropagation(); setDirection(-1); setPage(p => (p - 1 + count) % count); }}
                    aria-label="Previous"
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-[1202]
                                rounded-full bg-white/90 backdrop-blur p-3 shadow ring-1 ring-black/10 hover:bg-white"
                    >‹</button>
                    <button
                    onClick={(e) => { e.stopPropagation(); setDirection(1); setPage(p => (p + 1) % count); }}
                    aria-label="Next"
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-[1202]
                                rounded-full bg-white/90 backdrop-blur p-3 shadow ring-1 ring-black/10 hover:bg-white"
                    >›</button>
                </>
                )}
            </m.div>
            </AnimatePresence>,
            document.body
        )
        }
    </div>
  );
}
